/**
 * Flat SVG animal-face avatars, in the app palette. Emoji avatars (🦁🐼🐯🐰🐸🦊) render
 * inconsistently across platforms (Windows/Brave clip the 3D glyphs; iPad renders differently),
 * so every profile picks one of these instead.
 *
 * Every avatar shares a frame: a soft-coloured disc filling the viewBox, with the face
 * centred and occupying most of it (ears may overlap the disc edge slightly). Line work is
 * a consistent 2.5px round-cap stroke in INK or BROWN, matching the Shizi mascot.
 */
import type { ReactElement } from 'react';

export const AVATAR_IDS = ['bear', 'panda', 'tiger', 'rabbit', 'frog', 'fox'] as const;
export type AvatarId = (typeof AVATAR_IDS)[number];

const LEGACY_EMOJI: Record<string, AvatarId> = {
  '🦁': 'bear',
  '🐼': 'panda',
  '🐯': 'tiger',
  '🐰': 'rabbit',
  '🐸': 'frog',
  '🦊': 'fox',
};

/** Old profiles may still have an emoji (or anything else) stored as `avatar`; map it to an id. */
export function normalizeAvatar(s: string): AvatarId {
  if ((AVATAR_IDS as readonly string[]).includes(s)) return s as AvatarId;
  if (s === 'lion') return 'bear';
  return LEGACY_EMOJI[s] ?? 'bear';
}

// App palette (see apps/web/src/styles.css @theme).
const AMBER = '#F2A93B';
const AMBER_DEEP = '#D98C1F';
const AMBER_SOFT = '#FDECCB';
const CREAM = '#FBD98F';
const INK = '#1E2433';
const BROWN = '#5B4636';
const WHITE = '#FFFFFF';
const PINK = '#F9C9B4';
const PINK_DEEP = '#F5B8A8';
const GREEN = '#35A96B';
const GREEN_SOFT = '#DFF4E8';
const GREEN_PALE = '#EAF8EE';
const CREAM_MUZZLE = '#FDF3DC';
const PANDA_BG = '#DDF1F4';
const RABBIT_BG = '#FCE3E0';
const BEAR_BROWN = '#A9743F';
const BEAR_EAR_LIGHT = '#D9AE78';
const PANDA_PATCH = '#2B2F3A';

const STROKE = '2.5';

/** The mane ring geometry from the Shizi mascot: 12 overlapping circles hugging the head. */
const MANE = Array.from({ length: 12 }, (_, i) => {
  const a = ((i * 30 - 90) * Math.PI) / 180;
  return { x: 60 + 34 * Math.cos(a), y: 60 + 34 * Math.sin(a) };
});

/** Every avatar sits on a soft coloured disc filling the viewBox. */
function Disc({ fill }: { fill: string }) {
  return <circle cx="60" cy="60" r="58" fill={fill} />;
}

/** Two dot eyes with a highlight, the shared building block for every face. */
function Eyes({
  cx1,
  cx2,
  cy,
  r = 4.5,
  color = INK,
}: {
  cx1: number;
  cx2: number;
  cy: number;
  r?: number;
  color?: string;
}) {
  return (
    <>
      <circle cx={cx1} cy={cy} r={r} fill={color} />
      <circle cx={cx1 + r * 0.35} cy={cy - r * 0.35} r={r * 0.3} fill={WHITE} />
      <circle cx={cx2} cy={cy} r={r} fill={color} />
      <circle cx={cx2 + r * 0.35} cy={cy - r * 0.35} r={r * 0.3} fill={WHITE} />
    </>
  );
}

function BearFace() {
  return (
    <>
      <Disc fill={AMBER_SOFT} />
      <circle cx="30" cy="30" r="14" fill={BEAR_BROWN} />
      <circle cx="90" cy="30" r="14" fill={BEAR_BROWN} />
      <circle cx="30" cy="31" r="7" fill={BEAR_EAR_LIGHT} />
      <circle cx="90" cy="31" r="7" fill={BEAR_EAR_LIGHT} />
      <circle cx="60" cy="64" r="33" fill={BEAR_BROWN} />
      <ellipse cx="60" cy="79" rx="18" ry="14" fill={CREAM_MUZZLE} />
      <circle cx="41" cy="66" r="5" fill={PINK} opacity="0.8" />
      <circle cx="79" cy="66" r="5" fill={PINK} opacity="0.8" />
      <Eyes cx1={48} cx2={72} cy={56} r={4.5} />
      <ellipse cx="60" cy="72" rx="4.2" ry="3" fill={INK} />
      <path
        d="M53 80 Q56 84 60 81 Q64 84 67 80"
        stroke={INK}
        strokeWidth={STROKE}
        strokeLinecap="round"
        fill="none"
      />
    </>
  );
}

function PandaFace() {
  return (
    <>
      <Disc fill={PANDA_BG} />
      <circle cx="34" cy="32" r="11" fill={INK} />
      <circle cx="86" cy="32" r="11" fill={INK} />
      <circle cx="60" cy="64" r="36" fill={WHITE} stroke={INK} strokeWidth={STROKE} />
      <ellipse cx="47" cy="62" rx="11" ry="14" transform="rotate(-20 47 62)" fill={PANDA_PATCH} />
      <ellipse cx="73" cy="62" rx="11" ry="14" transform="rotate(20 73 62)" fill={PANDA_PATCH} />
      <circle cx="45" cy="58" r="5.5" fill={WHITE} />
      <circle cx="75" cy="58" r="5.5" fill={WHITE} />
      <circle cx="45" cy="58" r="2.8" fill={INK} />
      <circle cx="75" cy="58" r="2.8" fill={INK} />
      <circle cx="43.5" cy="56.5" r="1" fill={WHITE} />
      <circle cx="73.5" cy="56.5" r="1" fill={WHITE} />
      <ellipse cx="60" cy="72" rx="4" ry="3" fill={INK} />
      <path
        d="M55 78 Q60 84 65 78"
        stroke={INK}
        strokeWidth={STROKE}
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="38" cy="74" r="4" fill={PINK} opacity="0.8" />
      <circle cx="82" cy="74" r="4" fill={PINK} opacity="0.8" />
    </>
  );
}

function TigerFace() {
  return (
    <>
      <Disc fill={AMBER_SOFT} />
      <circle cx="60" cy="62" r="30" fill={AMBER} />
      <circle cx="30" cy="32" r="11" fill={AMBER} stroke={INK} strokeWidth="1.6" />
      <circle cx="90" cy="32" r="11" fill={AMBER} stroke={INK} strokeWidth="1.6" />
      <circle cx="30" cy="33" r="5.5" fill={PINK} />
      <circle cx="90" cy="33" r="5.5" fill={PINK} />
      <ellipse cx="60" cy="76" rx="15" ry="11" fill={CREAM_MUZZLE} />
      <path d="M52 40 q1 5 -1 9" stroke={BROWN} strokeWidth="3" strokeLinecap="round" />
      <path d="M60 38 q0 6 0 11" stroke={BROWN} strokeWidth="3" strokeLinecap="round" />
      <path d="M68 40 q-1 5 1 9" stroke={BROWN} strokeWidth="3" strokeLinecap="round" />
      <path d="M38 51 q5 3 3 9" stroke={BROWN} strokeWidth="3" strokeLinecap="round" />
      <path d="M40 62 q5 2 4 8" stroke={BROWN} strokeWidth="3" strokeLinecap="round" />
      <path d="M82 51 q-5 3 -3 9" stroke={BROWN} strokeWidth="3" strokeLinecap="round" />
      <path d="M80 62 q-5 2 -4 8" stroke={BROWN} strokeWidth="3" strokeLinecap="round" />
      <Eyes cx1={48} cx2={72} cy={56} r={4} />
      <path d="M56 68 Q60 65 64 68 Q60 73 56 68 Z" fill={INK} />
      <path
        d="M53 75 Q56 78 60 76 Q64 78 67 75"
        stroke={INK}
        strokeWidth={STROKE}
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="49" cy="80" r="1.2" fill={BROWN} />
      <circle cx="71" cy="80" r="1.2" fill={BROWN} />
    </>
  );
}

function RabbitFace() {
  return (
    <>
      <Disc fill={RABBIT_BG} />
      <ellipse cx="42" cy="22" rx="9" ry="26" fill={WHITE} stroke={INK} strokeWidth={STROKE} />
      <ellipse cx="78" cy="22" rx="9" ry="26" fill={WHITE} stroke={INK} strokeWidth={STROKE} />
      <ellipse cx="42" cy="24" rx="4" ry="17" fill={PINK_DEEP} />
      <ellipse cx="78" cy="24" rx="4" ry="17" fill={PINK_DEEP} />
      <circle cx="60" cy="68" r="30" fill={WHITE} stroke={INK} strokeWidth={STROKE} />
      <circle cx="43" cy="74" r="5.5" fill={PINK} opacity="0.85" />
      <circle cx="77" cy="74" r="5.5" fill={PINK} opacity="0.85" />
      <Eyes cx1={49} cx2={71} cy={62} r={4.5} />
      <circle cx="60" cy="72" r="3" fill={PINK_DEEP} />
      <path
        d="M53 77 Q56 81 60 78 Q64 81 67 77"
        stroke={INK}
        strokeWidth={STROKE}
        strokeLinecap="round"
        fill="none"
      />
      <rect
        x="55.5"
        y="79"
        width="4"
        height="5.5"
        rx="1"
        fill={WHITE}
        stroke={INK}
        strokeWidth="1.6"
      />
      <rect
        x="60.5"
        y="79"
        width="4"
        height="5.5"
        rx="1"
        fill={WHITE}
        stroke={INK}
        strokeWidth="1.6"
      />
    </>
  );
}

function FrogFace() {
  return (
    <>
      <Disc fill={GREEN_SOFT} />
      <ellipse cx="60" cy="66" rx="34" ry="28" fill={GREEN} />
      <ellipse cx="60" cy="88" rx="19" ry="11" fill={GREEN_PALE} />
      <circle cx="40" cy="38" r="14" fill={WHITE} stroke={INK} strokeWidth={STROKE} />
      <circle cx="80" cy="38" r="14" fill={WHITE} stroke={INK} strokeWidth={STROKE} />
      <circle cx="40" cy="38" r="6.5" fill={INK} />
      <circle cx="80" cy="38" r="6.5" fill={INK} />
      <circle cx="42.5" cy="35" r="2" fill={WHITE} />
      <circle cx="82.5" cy="35" r="2" fill={WHITE} />
      <circle cx="37" cy="70" r="5" fill={PINK} opacity="0.8" />
      <circle cx="83" cy="70" r="5" fill={PINK} opacity="0.8" />
      <path
        d="M42 76 Q60 92 78 76"
        stroke={INK}
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
    </>
  );
}

function FoxFace() {
  return (
    <>
      <Disc fill={AMBER_SOFT} />
      <path d="M24 40 L36 8 L48 38 Z" fill={BROWN} />
      <path d="M96 40 L84 8 L72 38 Z" fill={BROWN} />
      <path d="M28 36 L36 18 L44 35 Z" fill={CREAM} />
      <path d="M92 36 L84 18 L76 35 Z" fill={CREAM} />
      <circle cx="60" cy="62" r="30" fill="#E28A2B" />
      <circle cx="37" cy="68" r="13" fill={WHITE} />
      <circle cx="83" cy="68" r="13" fill={WHITE} />
      <ellipse cx="60" cy="82" rx="19" ry="13" fill={WHITE} />
      <Eyes cx1={48} cx2={72} cy={54} r={4.2} />
      <path d="M56 66 Q60 63 64 66 Q60 71 56 66 Z" fill={INK} />
    </>
  );
}

const FACES: Record<AvatarId, () => ReactElement> = {
  bear: BearFace,
  panda: PandaFace,
  tiger: TigerFace,
  rabbit: RabbitFace,
  frog: FrogFace,
  fox: FoxFace,
};

export function Avatar({
  id,
  size = 64,
  className = '',
}: {
  id: AvatarId;
  size?: number;
  className?: string;
}) {
  const Face = FACES[id] ?? BearFace;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={`shrink-0 select-none ${className}`}
      aria-hidden
    >
      <Face />
    </svg>
  );
}
